import contactFormBg1 from '@/assets/images/contact/contact-form-bg-1.jpg'
import contactFormBg2 from '@/assets/images/contact/contact-form-bg-2.jpg'
import contactFormBg3 from '@/assets/images/contact/contact-form-bg-3.jpg'
import { Form } from '@/components/Form'
import { Spinner } from '@/components/Spinner'
import { pageMaxWidth, pageXPadding, pageYPadding } from '@/config'
import { Button } from '@/features/public/home/components/Button'
import { HTMLInputTypeAttribute, ReactNode } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import * as zod from 'zod'
import { useSendMessage } from '../api/sendMessage'

const schema = zod.object({
  fullName: zod.string().min(1, { message: 'Please enter your full name' }),
  email: zod.string().email({ message: 'Please enter a valid email address' }),
  contactNumber: zod
    .string()
    .length(10, { message: 'Please enter a valid contact number' }),
  message: zod.string().min(1, { message: 'Please enter a message' }),
})
export type ContactFormValues = {
  fullName: string
  email: string
  contactNumber: string
  message: string
}
export const ContactForm = () => {
  const sendMessage = useSendMessage()
  return (
    <section className={`${pageXPadding} ${pageYPadding} bg-[#ECECEC]`}>
      <div
        className={`${pageMaxWidth} mx-auto flex flex-col gap-y-[150px] lg:flex-row lg:items-center lg:gap-y-0`}
      >
        <div className="relative flex h-[688px]  items-center justify-center md:h-[900px] lg:h-[800px] lg:w-[70%]">
          <div className="relative aspect-square w-full max-w-[400px]  border-4 border-[#D4AF37] md:w-4/5 md:max-w-[600px] lg:max-w-[406px]">
            <img
              className="absolute bottom-1/2  left-1/2 aspect-square w-4/5 -translate-x-1/2 object-cover drop-shadow-lg lg:left-auto lg:right-0 lg:w-4/5 lg:translate-x-1/2"
              src={contactFormBg3}
            />

            <Form<ContactFormValues, typeof schema>
              schema={schema}
              onSubmit={async (data, e) => {
                sendMessage.mutate(data, {
                  onSuccess: () => e?.target.reset(),
                })
              }}
              className="absolute left-1/2 top-[60%] flex w-4/5 -translate-x-1/2 flex-col gap-y-[18px] rounded-[17px] bg-[#F5F5F5] px-[25px] py-[15px] font-nunito drop-shadow-lg md:top-[70%] md:gap-y-[30px] lg:left-0 lg:top-[30%] lg:w-full lg:gap-y-[50px]"
            >
              {({ register }) => (
                <>
                  <h2 className="text-[16px] font-bold leading-[19.2px]  text-[#333333] md:text-[20px] md:leading-[24px] lg:text-[24px] lg:leading-[28.8px]">
                    Leave a message
                  </h2>
                  <div className="flex flex-col gap-y-[15px] md:gap-y-[20px] lg:gap-y-[24px]">
                    <InputField
                      registration={register('fullName')}
                      label="Full Name"
                    />
                    <InputField
                      registration={register('email')}
                      label="Email Address"
                      type="email"
                    />
                    <InputField
                      registration={register('contactNumber')}
                      label="Contact Number"
                      type="number"
                    />
                    <TextAreaField
                      registration={register('message')}
                      label="Message"
                    />
                  </div>
                  <Button type="submit" variant="tertiary">
                    <div className="flex gap-x-4">
                      {sendMessage.isPending ? (
                        <Spinner className="text-white" size="sm" />
                      ) : (
                        ''
                      )}
                      Submit
                    </div>
                  </Button>
                </>
              )}
            </Form>
          </div>
        </div>
        <div className="flex flex-col items-end gap-y-[12px] lg:w-[30%]">
          <img
            src={contactFormBg1}
            className="aspect-square max-h-[310px] w-full object-cover drop-shadow-lg md:max-h-[400px]"
          />
          <img
            src={contactFormBg2}
            className="aspect-square max-h-[310px] w-1/2 object-cover drop-shadow-lg"
          />
        </div>
      </div>
    </section>
  )
}

type InputFieldProps = {
  label: string
  type?: HTMLInputTypeAttribute
  registration: Partial<UseFormRegisterReturn>
}

type FormFieldWrapperProps = {
  children: ReactNode
  className?: string
}
const FormFieldWrapper = ({ children, className }: FormFieldWrapperProps) => {
  return (
    <div
      className={
        (twMerge(
          `flex flex-col text-[12px] leading-[14.4px] text-[#333333] md:text-[14px] md:leading-[16.8px] lg:text-[16px] lg:leading-[19.2px]`
        ),
        className)
      }
    >
      {children}
    </div>
  )
}
const InputField = ({
  label,
  type = 'text',
  registration,
}: InputFieldProps) => {
  return (
    <FormFieldWrapper>
      <label className=" font-[500]">{label}</label>
      <input
        {...registration}
        type={type}
        className="w-full border-b border-[#333333] bg-transparent pt-[8px] font-normal outline-none transition-all  focus:border-[#D4AF37]"
      />
    </FormFieldWrapper>
  )
}

type TextAreaFieldProps = {
  label: string
  registration: Partial<UseFormRegisterReturn>
}
const TextAreaField = ({ label, registration }: TextAreaFieldProps) => {
  return (
    <FormFieldWrapper>
      <label>{label}</label>
      <textarea
        {...registration}
        rows={2}
        className="w-full resize-none border-b border-[#333333] bg-transparent pt-[8px] font-normal outline-none transition-all  focus:border-[#D4AF37]"
      ></textarea>
    </FormFieldWrapper>
  )
}
